/**
 * 304 (FED-71). Photos added straight to the Gallery — a file picker on the
 * Gallery for its admins, and the queue behind it that takes a folder of
 * hundreds.
 *
 * A Gallery upload IS a post (migration 304's header says why): one post per
 * photo, `gallery_only`, out of the feed, earning nothing. So this runs the
 * composer's own steps for each file and nothing of its own — preparePhoto
 * (the 2,000 px feed copy, EXIF stripped), uploadPhoto, then the server's
 * gallery_add_photo in place of create_post, then keepOriginal (the
 * full-size copy, the camera's date, the location), then the typed date
 * taken and the batch's tags, then One reads it. The permission row is the
 * server's: contest no, licence yes — the picker never calls set_photo_uses,
 * because that door is an opt-out and this is not one.
 *
 * Order matters in one place and the test asserts it: set_photo_taken runs
 * AFTER keepOriginal. keepOriginal's set_photo_source rewrites
 * taken_at_local from the EXIF (or to null) and knows nothing of the
 * precision; the typed date is the uploader's word and goes last.
 *
 * Hundreds of files: every file is hashed first (SHA-256 in the browser) and
 * gallery_known_photos() says which are already here, so a batch that was
 * stopped — a closed laptop, a dropped connection — is picked again and only
 * the rest go up. Three files in flight at a time: the re-encode is CPU, the
 * upload is bandwidth, and three keeps both busy on a laptop without
 * starving the tab.
 */
import { supabase } from './supabase'
import { readExif } from './exif'
import { keepOriginal, preparePhoto, uploadPhoto } from './photos'
import { setPhotoFeature } from './photoRoom'
import { describePostPhotos } from './feed'

export type TakenPrecision = 'day' | 'month' | 'year'

/** What the batch says about every photo in it. */
export type GalleryBatch = {
  /** A typed date for every photo that has none of its own; null = EXIF only. */
  taken: { on: string; precision: TakenPrecision } | null
  /** photo_facet_value ids to accept on every photo (a brand, a sector…). */
  valueIds: string[]
  /** The file's name, without its extension, as the caption when none is typed. */
  captionFromName: boolean
}

export type GalleryItemStatus = 'waiting' | 'known' | 'uploading' | 'added' | 'failed' | 'stopped'

export type GalleryItem = {
  id: string
  file: File
  name: string
  bytes: number
  /** SHA-256 hex of the file as picked; filled by hashItems. */
  sha: string | null
  /** The camera's wall clock from EXIF, if any ("2026-10-01T07:12:00"). */
  exifTaken: string | null
  /** Typed for this photo; wins over the batch's date. */
  taken: { on: string; precision: TakenPrecision } | null
  caption: string
  status: GalleryItemStatus
  error: string | null
  contentId: string | null
  path: string | null
}

const ACCEPT = /^image\/(jpeg|png|webp)$/

/** The picked files as queue items, images only, EXIF dates read. */
export async function itemsFromFiles(files: Iterable<File>): Promise<GalleryItem[]> {
  const out: GalleryItem[] = []
  for (const file of files) {
    if (!ACCEPT.test(file.type)) continue
    let exifTaken: string | null = null
    try {
      exifTaken = (await readExif(file)).takenAtLocal
    } catch {
      exifTaken = null
    }
    out.push({
      id: crypto.randomUUID(),
      file,
      name: file.name,
      bytes: file.size,
      sha: null,
      exifTaken,
      taken: null,
      caption: '',
      status: 'waiting',
      error: null,
      contentId: null,
      path: null,
    })
  }
  return out
}

export async function sha256Hex(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer())
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('')
}

/** Of these hashes, the ones already in a Gallery the caller administers. */
export async function galleryKnown(hashes: string[]): Promise<Set<string>> {
  const known = new Set<string>()
  for (let i = 0; i < hashes.length; i += 500) {
    const { data, error } = await supabase.rpc('gallery_known_photos', { p_sha256: hashes.slice(i, i + 500) })
    if (error) throw error
    for (const h of (data as string[] | null) ?? []) known.add(h)
  }
  return known
}

/**
 * Hash every waiting item and mark the ones already here as `known`. Returns
 * the items (the same objects, mutated) so the caller can re-render.
 */
export async function hashItems(items: GalleryItem[], onEach?: (done: number) => void): Promise<GalleryItem[]> {
  let n = 0
  for (const it of items) {
    if (it.status === 'waiting' && !it.sha) {
      try {
        it.sha = await sha256Hex(it.file)
      } catch {
        it.sha = null
      }
    }
    onEach?.(++n)
  }
  const hashes = items.filter((i) => i.status === 'waiting' && i.sha).map((i) => i.sha!)
  if (hashes.length) {
    const known = await galleryKnown(hashes)
    for (const it of items) if (it.status === 'waiting' && it.sha && known.has(it.sha)) it.status = 'known'
  }
  return items
}

export async function addGalleryPhoto(a: {
  path: string
  mimeType: string
  byteSize: number
  width: number
  height: number
  caption: string | null
  sourceName: string
  sha: string | null
}): Promise<string> {
  const { data, error } = await supabase.rpc('gallery_add_photo', {
    p_storage_path: a.path,
    p_mime_type: a.mimeType,
    p_byte_size: a.byteSize,
    p_width: a.width,
    p_height: a.height,
    p_caption: a.caption,
    p_source_name: a.sourceName,
    p_source_sha256: a.sha,
  })
  if (error) throw error
  return data as string
}

/** The uploader types when a photo was taken: a day, a month or a year. */
export async function setPhotoTaken(path: string, on: string, precision: TakenPrecision): Promise<void> {
  const { error } = await supabase.rpc('set_photo_taken', { p_storage_path: path, p_taken_on: on, p_precision: precision })
  if (error) throw error
}

/** A typed "1998" / "1998-06" / "1998-06-15" as the date the server stores and its precision. */
export function parseTaken(text: string): { on: string; precision: TakenPrecision } | null {
  const s = text.trim()
  let m = /^(\d{4})$/.exec(s)
  if (m) return { on: `${m[1]}-01-01`, precision: 'year' }
  m = /^(\d{4})-(\d{2})$/.exec(s)
  if (m) return { on: `${m[1]}-${m[2]}-01`, precision: 'month' }
  m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
  if (m) return { on: s, precision: 'day' }
  return null
}

/** "1998", "Jun 1998" or "15 Jun 1998" — only as much as was given. */
export function takenText(on: string, precision: TakenPrecision | 'minute', locale: 'en' | 'es'): string {
  const [y, mo, d] = on.slice(0, 10).split('-').map(Number)
  const date = new Date(Date.UTC(y, (mo || 1) - 1, d || 1))
  const tag = locale === 'es' ? 'es-CL' : 'en-CA'
  if (precision === 'year') return String(y)
  if (precision === 'month') return date.toLocaleDateString(tag, { year: 'numeric', month: 'short', timeZone: 'UTC' })
  return date.toLocaleDateString(tag, { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' })
}

export type GalleryProgress = {
  done: number
  total: number
  added: number
  known: number
  failed: number
  /** Phase 2: One reading what was added. */
  reading: { done: number; total: number } | null
}

export type GalleryRun = {
  /** Resolves when every file has been tried (or the run was stopped) AND One has read what landed. */
  finished: Promise<void>
  stop: () => void
}

function messageOf(e: unknown): string {
  if (e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string') {
    return (e as { message: string }).message
  }
  return String(e)
}

/**
 * Upload every `waiting` (or `failed`, on a retry) item: three at a time,
 * each the composer's steps, then One reads the posts two at a time. Items
 * are mutated in place and `onProgress` fires after every change; the
 * caller re-renders from the array.
 */
export function uploadToGallery(
  items: GalleryItem[],
  batch: GalleryBatch,
  ctx: { tenantId: string; personId: string },
  onProgress: (p: GalleryProgress) => void,
  opts: { concurrency?: number } = {},
): GalleryRun {
  let stopped = false
  const todo = items.filter((i) => i.status === 'waiting' || i.status === 'failed')
  for (const it of todo) {
    it.status = 'waiting'
    it.error = null
  }
  const progress: GalleryProgress = {
    done: 0,
    total: todo.length,
    added: 0,
    known: items.filter((i) => i.status === 'known').length,
    failed: 0,
    reading: null,
  }
  const tell = () => onProgress({ ...progress, reading: progress.reading ? { ...progress.reading } : null })

  async function one(it: GalleryItem): Promise<void> {
    it.status = 'uploading'
    tell()
    try {
      const photo = await preparePhoto(it.file)
      try {
        const att = await uploadPhoto(photo, ctx.tenantId, ctx.personId)
        const caption =
          it.caption.trim() || (batch.captionFromName ? it.name.replace(/\.[^.]+$/, '').replace(/[_]+/g, ' ').trim() : '')
        const contentId = await addGalleryPhoto({
          path: att.storage_path,
          mimeType: att.mime_type,
          byteSize: att.byte_size,
          width: att.width,
          height: att.height,
          caption: caption || null,
          sourceName: it.name,
          sha: it.sha,
        })
        it.contentId = contentId
        it.path = att.storage_path
        /* The full-size copy, the camera's date and the location, exactly as a
           licensed feed photo keeps them (146/149). Never throws. */
        await keepOriginal(photo, att.storage_path, { keepFull: true, camera: true, location: true })
        /* AFTER keepOriginal: the typed date is the uploader's word and goes last. */
        const taken = it.taken ?? (it.exifTaken ? null : batch.taken)
        if (taken) {
          try {
            await setPhotoTaken(att.storage_path, taken.on, taken.precision)
          } catch {
            /* a date, never a reason to lose the photo */
          }
        }
        for (const valueId of batch.valueIds) {
          try {
            await setPhotoFeature(att.storage_path, valueId, true)
          } catch {
            /* a tag, never a reason to lose the photo */
          }
        }
        it.status = 'added'
        progress.added++
      } finally {
        URL.revokeObjectURL(photo.previewUrl)
      }
    } catch (e) {
      it.status = 'failed'
      it.error = messageOf(e)
      progress.failed++
    }
    progress.done++
    tell()
  }

  const width = Math.max(1, Math.min(opts.concurrency ?? 3, 6))
  let next = 0
  async function worker(): Promise<void> {
    while (!stopped && next < todo.length) {
      const it = todo[next++]
      await one(it)
    }
  }

  const finished = (async () => {
    await Promise.all(Array.from({ length: width }, worker))
    for (const it of todo) {
      if (it.status === 'waiting') {
        it.status = 'stopped'
      }
    }
    tell()
    /* Phase 2: One reads what landed, two at a time. A photo it misses is
       read the next time somebody opens the Gallery (302), so this never fails the batch. */
    const added = todo.filter((i) => i.status === 'added' && i.contentId)
    if (added.length === 0) return
    progress.reading = { done: 0, total: added.length }
    tell()
    let r = 0
    async function reader(): Promise<void> {
      while (r < added.length) {
        const it = added[r++]
        await describePostPhotos(it.contentId!)
        progress.reading!.done++
        tell()
      }
    }
    await Promise.all([reader(), reader()])
  })()

  return {
    finished,
    stop: () => {
      stopped = true
    },
  }
}
